import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { IParams } from 'apis/GraphQL/Variables/hoPlan';
import HOPlanApi from 'apis/HOPlanApis';
import { PlantApi } from 'apis/PlantApis';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { ICreateHOPlan, IHOPlan } from 'interfaces/HOPlan/HOPlan';
import { IPlant } from 'interfaces/Plant/Plant';
import Content from 'layouts/Content';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_HO_REVENUE } from 'utils/constants/importId';
import FilterHOPlan from './FilterHOPlan';
import TableHOPLan from './TableHOPlan';

export type IHOTablePlan = {
  tplantName: string;
  tcreateUser: string;
  tmodifyUser: string;
} & Omit<
  IHOPlan,
  'approval' | 'tcreateUser' | 'tmodifyUser' | 'tplant' | 'trevenuePlanBranches'
>;

interface IFilter {
  year?: number;
  plantId?: string;
  typeRevenue?: string;
}

export interface IData {
  items: IHOTablePlan[];
  pageInfo?: any;
}

const { PlusOutlined } = icons;

const initData: IHOTablePlan = {
  id: '',
  plantId: '',
  tplantName: '',
  janSaleamt: 0,
  febSaleamt: 0,
  marSaleamt: 0,
  aprSaleamt: 0,
  maySaleamt: 0,
  junSaleamt: 0,
  julSaleamt: 0,
  augSaleamt: 0,
  sepSaleamt: 0,
  octSaleamt: 0,
  novSaleamt: 0,
  decSaleamt: 0,
  unitMoney: '',
  ttypeRevenue: { value: '' },
  typeRevenue: '',
  year: '',
  totalYearSaleamt: 0,
  createDate: '',
  tcreateUser: '',
  modifyDate: '',
  tmodifyUser: '',
};

const initStateData: IData = { items: [], pageInfo: {} };

const HOPlan = () => {
  const { user } = useUser(),
    navigate = useNavigate(),
    { setTitle, setBreadcrumb } = useTitle();

  const tableRef = useRef<any>(null);
  const filterFormRef = useRef<any>(null);

  const [filter, setFilter] = useState<IFilter>({}),
    [plants, setPlants] = useState<IPlant[]>([]),
    [data, setData] = useState<IData>(initStateData),
    [isOpenFilter, setIsOpenFilter] = useState(false),
    [selectedIndex, setSelectedIndex] = useState<number>(),
    [isAddRow, setIsAddRow] = useState(false),
    [isOpenImportModal, setIsOpenImportModal] = useState(false);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    page_size = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const year = new Date().getFullYear();

  const defaultFilter = useMemo(() => {
    if (user?.isCN)
      return {
        year: year,
        plantId: user?.plantCode,
        ...filter,
      };

    return {
      year: year,
      ...filter,
    };
  }, [filter]);

  const handleClearFilters = useCallback(() => {
    if (filterFormRef.current) {
      filterFormRef.current?.resetFields();
      filterFormRef.current?.setFieldValue('year', year);
    }

    setFilter({ year });
    navigate(`?page=1&page_size=${page_size}`);
  }, [navigate, page_size, year, filterFormRef]);

  const { isPending: getHOPlantsLoading, mutate: getHOPlants } = useMutation({
      mutationFn: (params: IParams | any) => {
        if (params?.type == 'export')
          return HOPlanApi.getRevenueHOPlanList({ ...params, ...filter });
        return HOPlanApi.getRevenueHOPlanList(params);
      },
      onSuccess: (res, variabls) => {
        const data: IHOPlan[] = res.data.getRevenueHo.items;
        const pageInfo = res.data.getRevenueHo.pageInfo;
        const newData = {
          items: data.map((item) => ({
            ...item,
            tplantName: item.tplant !== null ? item.tplant.name : '',
            tcreateUser:
              item.tcreateUser && item.tcreateUser !== null
                ? item.tcreateUser.fullName
                : '',
            tmodifyUser:
              item.tmodifyUser && item.tmodifyUser !== null
                ? item.tmodifyUser.fullName
                : '',
          })),
          pageInfo: pageInfo,
        };

        if (variabls.type == 'export') {
          exportExcel_v2(
            variabls.headerRow,
            newData.items.map((item) => ({
              ...item,
              unitMoney: item.ttypeRevenue.value === '%' ? '-' : item.unitMoney,
            })) || [],
            'Kế hoạch - HO Kế hoạch',
            true,
            2,
          );
        } else if (isAddRow) {
          const currentDate = new Date().toISOString();

          setData({
            ...newData,
            items: [
              {
                ...initData,
                createDate: currentDate,
                tcreateUser: user?.fullName || '',
              },
              ...newData.items,
            ],
          });
        } else setData(newData);
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: updateHOPlantLoading, mutate: updatePlant } = useMutation({
      mutationFn: (data: ICreateHOPlan[]) =>
        HOPlanApi.updateRevenueHOPlan(data),
      onSuccess: () => {
        getHOPlants({
          page: page,
          pageSize: page_size,
          ...defaultFilter,
        });
        setSelectedIndex(undefined);
        toastSuccess('Cập nhật thành công');
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: createHOPlantLoading, mutate: createPlant } = useMutation({
      mutationFn: (data: ICreateHOPlan[]) =>
        HOPlanApi.createRevenueHOPlan(data),
      onSuccess: () => {
        handleClearFilters();
        // getHOPlants({
        //   page: page,
        //   pageSize: page_size,
        // });
        setIsOpenImportModal(false);
        setSelectedIndex(undefined);
        toastSuccess('Tạo mới kế hoạch thành công');
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: deleteHOPlantLoading, mutate: deletePlant } = useMutation({
      mutationFn: (id: any) => HOPlanApi.deleteRevenueHOPlan(id),
      onSuccess: (res) => {
        toastSuccess('Xóa kế hoạch thành công');

        getHOPlants({
          page: page,
          pageSize: page_size,
          ...defaultFilter,
        });
      },
      onError: (error) => {
        console.log(error);
        toastError(error.message);
      },
    });
  const { isPending: getPlantsLoading, mutate: getAllPlant } = useMutation({
    mutationFn: () => PlantApi.getAllPlantFilterHO(),
    onSuccess: (res: any) => {
      setPlants(res.data?.getPlant.items || []);
    },
    onError: (error) => console.log(error),
  });

  useEffect(() => {
    setTitle('[HO] Đăng ký kế hoạch theo chi nhánh');
    setBreadcrumb([{ title: 'Kế hoạch' }, { title: '[HO] Kế hoạch' }]);

    getAllPlant();
    setSelectedIndex(undefined);
  }, [getAllPlant, setBreadcrumb, setTitle]);

  useEffect(() => {
    const params: IParams = {
      page: page,
      pageSize: page_size,
      ...defaultFilter,
    };
    getHOPlants(params);
  }, [page, page_size, filter]);

  useEffect(() => {
    if (isOpenFilter === false) return;
    handleCancel();
  }, [isOpenFilter]);

  useEffect(() => {
    if (selectedIndex === 0) {
      setIsOpenFilter(false);
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (!isAddRow) return;

    setSelectedIndex(0);
  }, [data]);

  const handleCreate = useCallback(() => {
    setIsAddRow(true);

    // const currentDate = new Date().toISOString();
    if (page === 1) {
      setData((prev: any) => ({
        ...prev,
        items: [
          {
            ...initData,
          },
          ...prev.items,
        ],
      }));
      setSelectedIndex(0);
      return;
    }

    navigate(`?page=1&page_size=${page_size}`);
  }, [data, page]);

  const handleCancel = useCallback(() => {
    const newData = data.items.filter((item) => {
      if (item.id === '') return;

      const currentYear =
        typeof item.year === 'object'
          ? dayjs(item.year, 'YYYY').year().toString()
          : item.year.toString();

      item.year = currentYear;
      return item;
    });
    setSelectedIndex(undefined);
    setData((prev: any) => ({ ...prev, items: newData }));
    setIsAddRow(false);
  }, [data]);

  const handleDelete = useCallback((record: any) => {
    deletePlant(record);
  }, []);

  const handleRowChange = useCallback(
    (record: any, value: any) => {
      setIsAddRow(false);

      const isUpdate = record.id !== '',
        newValue = {
          ...record,
          ...value,
          year: parseInt(value.year.toString()),
        };

      const {
        id,
        year,
        tplant,
        plantId,
        approval,
        tplantName,
        createDate,
        tcreateUser,
        modifyDate,
        tmodifyUser,
        ttypeRevenue,
        createId,
        unitMoney,
        ...others
      } = newValue;

      Object.keys(others).map((key) => {
        if (!isNaN(parseFloat(others[key])))
          others[key] = parseFloat(others[key]);
      });

      const newItem = { ...others };
      const keys = Object.keys(newItem);

      keys.map((key) => {
        if (
          (key.endsWith('Saleamt') || key.endsWith('PlanAmt')) &&
          newItem[key]
        ) {
          newItem[key] = newItem[key] ? parseFloat(newItem[key]) : null;
        }
      });

      if (isUpdate) {
        updatePlant({
          id,
          year,
          plantId,
          // modifyId: user?.id,
          // modifyDate: 'now()',
          ...newItem,
        });
        return;
      }
      createPlant({
        year: parseInt(year.toString()),
        plantId: plantId.toString(),
        // createId: user?.id,
        ...newItem,
      });
    },
    [data, user],
  );

  const handleImportData = useCallback(() => {
    setIsOpenImportModal(true);
  }, []);

  const handleExportExcel = useCallback(() => {
    if (tableRef && tableRef.current) tableRef.current.handleExportExcel();
  }, []);

  const handleFilter = (values: any) => {
    navigate(`?page=1&page_size=${page_size}`);

    const { branch, year, typeRevenue } = values;

    const currentYear =
      year && typeof year !== 'undefined'
        ? parseInt(year.year().toString())
        : year;

    setFilter({ plantId: branch, year: currentYear, typeRevenue: typeRevenue });
  };

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter
            isOpen={isOpenFilter}
            disabled={selectedIndex !== undefined}
            setIsOpen={setIsOpenFilter}
          />
        </Col>
        <Col>
          <ButtonExport
            disabled={selectedIndex !== undefined}
            onClick={handleExportExcel}
          />
        </Col>
        {(user?.isAdmin || user?.isHO) && (
          <>
            <Col>
              <ButtonImport
                disabled={selectedIndex !== undefined}
                onClick={handleImportData}
              />
            </Col>
            <Col>
              <Button
                fill
                title="Tạo"
                iconBefore={<PlusOutlined />}
                disabled={selectedIndex !== undefined}
                handleOnclick={handleCreate}
              />
            </Col>
          </>
        )}
      </Row>
      <FilterHOPlan
        plants={plants}
        ref={filterFormRef}
        isOpenFilter={isOpenFilter}
        onFinish={handleFilter}
        setIsOpenFilter={setIsOpenFilter}
      />
      <UploadModal
        importId={IMPORT_HO_REVENUE}
        open={isOpenImportModal}
        setOpen={setIsOpenImportModal}
        okTitle="Lưu"
        title={'Import'}
        templateFileName="Kế hoạch - HO Kế hoạch"
        refetch={() => {
          navigate(`?page=1&page_size=${page_size}`);
          getHOPlants({ page: 1, pageSize: page_size });
        }}
      />
      <Content>
        <TableHOPLan
          data={data}
          ref={tableRef}
          plants={plants}
          initData={initData}
          handleDelete={handleDelete}
          isAddRow={isAddRow}
          selectedIndex={selectedIndex}
          loading={
            getHOPlantsLoading ||
            updateHOPlantLoading ||
            createHOPlantLoading ||
            deleteHOPlantLoading ||
            getPlantsLoading
          }
          onCancel={handleCancel}
          setSelectedIndex={setSelectedIndex}
          onRowChange={handleRowChange}
          getHOPlants={getHOPlants}
        />
      </Content>
    </>
  );
};

export default HOPlan;
